import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import dayjs from "dayjs";
import {
  rejectAffiliate,
  returnAffiliateToPending,
  verifyAffiliate,
} from "../../repository/data.repository";
import { VerificationStatus } from "../../repository/data.default";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { getProblemMessage } from "../../utils/http";

export interface AffiliateVerificationPanelProps {
  readonly uid: string | undefined;
  readonly open: boolean;
  readonly status?: VerificationStatus;
  readonly verifiedAt?: string | null;
  readonly rejectedAt?: string | null;
  readonly rejectionReason?: string | null;
  readonly onChanged?: () => void;
}

// Admin brief 2026-11-04 — Verification workflow panel on Consultant/
// Collaborator view. Admin-only state machine: Pending → Verified | Rejected,
// and the admin can re-queue a rejected record for another review (ToPending).
function AffiliateVerificationPanel(props: AffiliateVerificationPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  // Mirrors the gate used on the documents panel — both are affiliate-admin
  // actions and should be available to the same people.
  const canManage =
    permissions.can("PlatformConsultants", "create") ||
    permissions.can("PlatformCollaborators", "create");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectForm] = Form.useForm();
  const [busy, setBusy] = useState(false);

  if (!props.uid || !props.open) return null;

  const status = props.status ?? VerificationStatus.Pending;

  async function onVerify(): Promise<void> {
    try {
      setBusy(true);
      await verifyAffiliate(props.uid!);
      message.success("Affiliate verified.");
      props.onChanged?.();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to verify.");
    } finally {
      setBusy(false);
    }
  }

  async function onReject(): Promise<void> {
    try {
      const v = await rejectForm.validateFields();
      setBusy(true);
      await rejectAffiliate(props.uid!, v.reason || undefined);
      message.success("Affiliate rejected.");
      setRejectOpen(false);
      rejectForm.resetFields();
      props.onChanged?.();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(getProblemMessage(err) || "Failed to reject.");
    } finally {
      setBusy(false);
    }
  }

  async function onReturnToPending(): Promise<void> {
    try {
      setBusy(true);
      await returnAffiliateToPending(props.uid!);
      message.success("Affiliate returned to Pending review.");
      props.onChanged?.();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to re-open.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>Verification</Typography.Text>
          <StatusTag status={status} />
        </Space>
      }
    >
      <Space direction="vertical" size={6} className="w-full">
        {status === VerificationStatus.Verified && props.verifiedAt && (
          <Typography.Text type="secondary" className="text-xs">
            Verified on {dayjs(props.verifiedAt).format("DD/MM/YYYY HH:mm")}
          </Typography.Text>
        )}
        {status === VerificationStatus.Rejected && props.rejectedAt && (
          <Space direction="vertical" size={2}>
            <Typography.Text type="secondary" className="text-xs">
              Rejected on {dayjs(props.rejectedAt).format("DD/MM/YYYY HH:mm")}
            </Typography.Text>
            {props.rejectionReason && (
              <Typography.Text className="text-xs">
                Reason: {props.rejectionReason}
              </Typography.Text>
            )}
          </Space>
        )}

        {canManage && (
          <Space wrap>
            {status !== VerificationStatus.Verified && (
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={busy}
                onClick={onVerify}
              >
                Verify
              </Button>
            )}
            {status !== VerificationStatus.Rejected && (
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            )}
            {status !== VerificationStatus.Pending && (
              <Button
                size="small"
                icon={<UndoOutlined />}
                loading={busy}
                onClick={onReturnToPending}
              >
                Move back to Pending
              </Button>
            )}
          </Space>
        )}
      </Space>

      <Modal
        open={rejectOpen}
        title="Reject affiliate"
        okText="Reject"
        okButtonProps={{ danger: true }}
        confirmLoading={busy}
        onCancel={() => setRejectOpen(false)}
        onOk={onReject}
        destroyOnClose
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason (optional)"
            tooltip="Shown to future admins. The affiliate does not see this."
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

function StatusTag(props: { status: VerificationStatus }) {
  switch (props.status) {
    case VerificationStatus.Verified:
      return <Tag color="green" icon={<CheckCircleOutlined />}>Verified</Tag>;
    case VerificationStatus.Rejected:
      return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;
    default:
      return <Tag color="gold" icon={<ClockCircleOutlined />}>Pending</Tag>;
  }
}

export default AffiliateVerificationPanel;
